import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { utilsService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";
import {
  Container,
  WrapperRow,
  Title,
  BodyText,
  GreenDot,
  Greentext,
  StateWrapper,
} from "../../../cloud/Instances/styles";
import { BackArrow, TopProgress } from "../../../../components";
import { timeConverterMMDDYYHHMM } from "../../../../utils/dates";
import { handleStatusName } from "../../../../utils/handle-status-name";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  EmptyValue,
  Label,
  Mono,
  SecondaryValue,
  Stack,
  Value,
} from "../styles";
import {
  InfoGrid,
  InfoItem,
  Section,
  SectionTitle,
  RawBlock,
} from "./styles";

type TaskLogDetails = {
  id?: string;
  parent_id?: string | null;
  name?: string | null;
  description?: string | null;
  state?: string | null;
  created_at?: string | null;
  last_update?: string | null;
  resource_id?: string | number | null;
  resource_type?: string | null;
  args?: string | null;
  kwargs?: string | null;
  has_subtasks?: boolean;
  operations?: any[];
  [key: string]: any;
};

const PeriodicTaskLogDetails: FC = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  usePageTitle(t("PERIODIC_TASK_DETAILS"));
  const [loading, setLoading] = useState<boolean>(true);
  const [task, setTask] = useState<TaskLogDetails | null>(null);

  const renderState = useCallback(
    (state?: string | null) => {
      if (!state) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <StateWrapper>
          <GreenDot active={state} />
          <Greentext style={{ textTransform: "capitalize" }} active={state}>
            {handleStatusName(state, t)}
          </Greentext>
        </StateWrapper>
      );
    },
    [t]
  );

  const renderTask = useCallback(
    (item: TaskLogDetails) => {
      if (!item.name && !item.description) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Stack>
          {item.name ? <Value title={item.name}>{item.name}</Value> : null}
          {item.description ? (
            <SecondaryValue title={item.description}>
              {item.description}
            </SecondaryValue>
          ) : null}
        </Stack>
      );
    },
    [t]
  );

  const renderResource = useCallback(
    (item: TaskLogDetails) => {
      if (!item.resource_type && !item.resource_id) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Stack>
          {item.resource_type ? (
            <Value title={item.resource_type}>{item.resource_type}</Value>
          ) : null}
          {item.resource_id ? (
            <SecondaryValue>#{item.resource_id}</SecondaryValue>
          ) : null}
        </Stack>
      );
    },
    [t]
  );

  const renderPayload = useCallback(
    (args?: string | null, kwargs?: string | null) => {
      if (!args && !kwargs) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Stack>
          {args ? (
            <Stack>
              <Label>{t("ARGS")}</Label>
              <Mono>{args}</Mono>
            </Stack>
          ) : null}
          {kwargs ? (
            <Stack>
              <Label>{t("KWARGS")}</Label>
              <Mono>{kwargs}</Mono>
            </Stack>
          ) : null}
        </Stack>
      );
    },
    [t]
  );

  const fetchTask = useCallback(() => {
    if (!taskId) {
      navigate("/utilities/periodic-task-log");
      return;
    }

    setLoading(true);
    utilsService
      .getTaskLog(taskId)
      .then((res) => {
        setTask(res.data || null);
      })
      .catch((error) => {
        console.error("Failed to fetch periodic task log details", error);
        viewAlert({
          severity: "error",
          message:
            error?.response?.data?.detail || t("TASK_LOG_LOAD_FAILED"),
        });
        navigate("/utilities/periodic-task-log");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, taskId, t, viewAlert]);

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const generalInfo = useMemo(
    () => [
      {
        label: t("TASK_ID"),
        value: task?.id ? (
          <Value title={task.id}>{task.id}</Value>
        ) : (
          <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
        ),
      },
      {
        label: t("PARENT_TASK_ID"),
        value: task?.parent_id ? (
          <Value title={task.parent_id}>{task.parent_id}</Value>
        ) : (
          <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
        ),
      },
      {
        label: t("STATE"),
        value: renderState(task?.state),
      },
      {
        label: t("CREATED_AT"),
        value: task?.created_at ? (
          timeConverterMMDDYYHHMM(task.created_at)
        ) : (
          <EmptyValue>—</EmptyValue>
        ),
      },
      {
        label: t("LAST_UPDATE"),
        value: task?.last_update ? (
          timeConverterMMDDYYHHMM(task.last_update)
        ) : (
          <EmptyValue>—</EmptyValue>
        ),
      },
      {
        label: t("RESOURCE"),
        value: task ? renderResource(task) : <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>,
      },
      {
        label: t("TASK"),
        value: task ? renderTask(task) : <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>,
      },
      {
        label: t("HAS_SUBTASKS"),
        value:
          typeof task?.has_subtasks === "boolean" ? (
            task.has_subtasks ? (
              t("YES")
            ) : (
              t("NO")
            )
          ) : (
            <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
          ),
      },
    ],
    [renderResource, renderState, renderTask, t, task]
  );

  return (
    <Container>
      {loading ? <TopProgress loading={loading} /> : null}
      <BackArrow fallbackPath="/utilities/periodic-task-log" />
      <WrapperRow>
        <Title>{t("PERIODIC_TASK_DETAILS")}</Title>
      </WrapperRow>
      {loading ? (
        <WrapperRow style={{ marginTop: "20px" }}>
          <BodyText>{t("LOADING")}...</BodyText>
        </WrapperRow>
      ) : task ? (
        <>
          <Section>
            <SectionTitle>{t("GENERAL_INFORMATION")}</SectionTitle>
            <InfoGrid>
              {generalInfo.map((item) => (
                <InfoItem key={item.label}>
                  <Label>{item.label}</Label>
                  {item.value}
                </InfoItem>
              ))}
            </InfoGrid>
          </Section>
          <Section>
            <SectionTitle>{t("PAYLOAD")}</SectionTitle>
            {renderPayload(task.args, task.kwargs)}
          </Section>
          <Section>
            <SectionTitle>{t("RAW_RESPONSE")}</SectionTitle>
            <RawBlock>
              {JSON.stringify(task, null, 2)}
            </RawBlock>
          </Section>
        </>
      ) : (
        <WrapperRow style={{ marginTop: "20px" }}>
          <BodyText>{t("NOT_AVAILABLE")}</BodyText>
        </WrapperRow>
      )}
    </Container>
  );
};

export default PeriodicTaskLogDetails;
